import { Storage } from './model/storage.model';

export class IndexedDBStorage implements Storage {
  private readonly dbName = 'IndexedDB';
  private readonly storeName = 'ResApp';
  private readonly version = 1;
  private dbReady: Promise<IDBDatabase>;

  constructor() {
    this.dbReady = this.openDB();
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      // eslint-disable-next-line  @typescript-eslint/no-unused-vars
      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async openObjectStoreTransaction(
    mode: IDBTransactionMode
  ): Promise<IDBObjectStore> {
    const db = await this.dbReady;
    const tx = db.transaction(this.storeName, mode);
    const store = tx.objectStore(this.storeName);
    return store;
  }

  async has(key: string): Promise<boolean> {
    const store = await this.openObjectStoreTransaction('readonly');
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result !== undefined);
      request.onerror = () => reject(request.error);
    });
  }

  async get(key: string): Promise<string | null> {
    const store = await this.openObjectStoreTransaction('readonly');
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => {
        const result = request.result;
        resolve(typeof result === 'string' ? result : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async set(key: string, data: string): Promise<boolean> {
    const store = await this.openObjectStoreTransaction('readwrite');
    return new Promise((resolve, reject) => {
      const request = store.put(data, key);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async remove(key: string): Promise<boolean> {
    const store = await this.openObjectStoreTransaction('readwrite');
    return new Promise((resolve, reject) => {
      const request = store.delete(key);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async getKeys(): Promise<string[]> {
    const store = await this.openObjectStoreTransaction('readwrite');
    return new Promise((resolve, reject) => {
      const request = store.getAllKeys();
      request.onsuccess = () => {
        const keys = request.result.map((key) => key.toString()); // primary keys can be string | number
        resolve(keys);
      };
      request.onerror = () => reject(request.error);
    });
  }
}
